import { Activity, ChartLine, Files, Info } from "lucide-react";

import {
  EntityHeader,
  EntityName,
  EntityTabs,
  type EntityTabItem,
  useActiveEntityTab,
} from "@/shared/components/EntityPage";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks/useCheckFeatureFlag";
import { type SessionRecord } from "@/shared/domain/sessions";
import { SessionTab } from "@/shared/navigation";

import { SessionHeader } from "./SessionHeader";

export interface SessionChromeProps {
  /** URL-derived session id. Used as the heading when ``session`` is ``null``. */
  sessionId: string;
  /**
   * Resolved session record, or ``null`` when the parent's ``useSession``
   * fetch is pending or failed. When ``null`` the heading falls back to
   * ``sessionId`` and the rest of the header is omitted; the tab strip
   * renders unconditionally because it reads its active value from the URL.
   */
  session: SessionRecord | null;
}

/**
 * Header and tab bar for ``/sessions/:sessionId``. Rendered on both the
 * success and error branches so the user sees the same heading hierarchy
 * whether or not the session record resolved.
 */
export function SessionChrome({ sessionId, session }: SessionChromeProps) {
  // Same flag gates the /sessions/:sessionId/metrics route in SessionsRoutes.
  const metricsTabEnabled = useCheckFeatureFlag("release-metrics");
  const tabs: EntityTabItem<SessionTab>[] = [
    { value: SessionTab.Topics, label: "Topics", icon: <Activity /> },
    { value: SessionTab.Sources, label: "Sources", icon: <Files /> },
    ...(metricsTabEnabled
      ? [{ value: SessionTab.Metrics, label: "Metrics", icon: <ChartLine /> }]
      : []),
    { value: SessionTab.Details, label: "Details", icon: <Info /> },
  ];
  // The tab reads from the URL alone, so the strip renders on the error
  // branch too, before or without the session record.
  const activeTab = useActiveEntityTab(tabs);

  return (
    <div>
      {session ? (
        <SessionHeader session={session} />
      ) : (
        <EntityHeader
          type="session"
          name={<EntityName>{sessionId}</EntityName>}
        />
      )}
      <EntityTabs tabs={tabs} activeTab={activeTab} aria-label="session tabs" />
    </div>
  );
}
